import { CACHE_ENDPOINT, V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useQuery } from "@tanstack/react-query";

export interface FundsMovementLeg {
  amountField?: string;
  payloadAmountField?: string;
  tokenField?: string;
  payloadTokenField?: string;
  chainField?: string;
  payloadChainField?: string;
  chainIdValue?: number;
  tokenAddressValue?: string;
  nativeAsset?: boolean;
  tokenLabel?: string;
  /** Dynamic token label from result (e.g., "inputSymbol" resolves to "SOL" at runtime) */
  tokenLabelField?: string;
  /** Override token decimals. Use 0 when the payload amount is already human-readable (e.g. dollars). */
  decimals?: number;
  /**
   * Skip this leg when a payload field matches a specific value. Used for actions
   * where the same `amount` field is denominated differently based on a mode
   * switch — e.g. morpho-vault-withdraw's `withdrawType: "assets"` means the
   * amount is in underlying asset units, so checking it against vault-shares
   * balance is meaningless. Matching is stringified equality.
   */
  skipWhen?: { field: string; value: string };
}

export interface FundsMovementMetadata {
  kind: "send" | "swap" | "bridge" | "disperse" | "receive";
  sent?: FundsMovementLeg | FundsMovementLeg[];
  received?: FundsMovementLeg | FundsMovementLeg[];
  recipientField?: string;
  recipientsField?: string;
}

// Action/Trigger definition type
export interface ActionTriggerDefinition {
  type: string; // e.g., "token-price-mock", "schedule", etc.
  name: string;
  description: string;
  platform?: string;
  icon?: string;
  logoUrl?: string; // Optional logo URL for custom icons
  category?: "action" | "trigger" | "logic"; // Used to distinguish logic nodes from action/trigger nodes
  payloadSchema: {
    type: "object";
    properties: Record<string, any>;
    required?: string[];
    additionalProperties?: boolean;
  };
  resultSchema?: {
    type: "object";
    properties: Record<string, any>;
    required?: string[];
    additionalProperties?: boolean;
  };
  waitPayloadSchema?: {
    type: "object";
    properties: Record<string, any>;
    required?: string[];
    additionalProperties?: boolean;
  };
  connector?: {
    type: string; // e.g., "turnkey"
    accountType?: string; // e.g., "polymarket_deposit_wallet"
  };
  tags?: string[]; // Optional tags for categorization
  requiresGas?: boolean; // Whether action requires gas (blockchain transaction fees)
  gasChainId?: number; // Chain ID where gas is needed (e.g., 137 for Polygon)
  requiredTokens?: Array<{
    address: string;
    chainId: number;
    symbol: string;
    reason?: string;
  }>;
  fundsMovement?: FundsMovementMetadata;
  test?: {
    enabled: boolean;
    disabledReason?: string;
  };
  // x402 endpoint metadata for node display (used by mergeX402MetadataToPayload in onAddNode)
  _endpointMetadata?: {
    name?: string;
    description?: string;
    url?: string;
    price?: string;
    network?: string;
    tokenSymbol?: string;
    tokenDecimals?: number;
  };
}

interface DefinitionsApiResponse {
  code: number;
  message: string;
  data: ActionTriggerDefinition[];
}

interface SingleDefinitionApiResponse {
  code: number;
  message: string;
  data: ActionTriggerDefinition;
}

export type DefinitionType = "action" | "trigger" | "template" | "connector_def";

const definitionEndpoints: Record<DefinitionType, string> = {
  action: "/v1/actions",
  trigger: "/v1/triggers",
  template: "/v1/templates",
  connector_def: "/v1/connector-types",
};

export async function fetchDefinitionById(type: DefinitionType, id: string): Promise<ActionTriggerDefinition> {
  const endpoint = definitionEndpoints[type];
  if (!endpoint) {
    throw new Error(`Unknown definition type: ${type}`);
  }

  const response = await authenticatedFetch<SingleDefinitionApiResponse>(`${V2_ENDPOINT}${endpoint}/${id}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${type}: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

// Hook for fetching triggers
export function useTriggers(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: ["triggers"],
    queryFn: async () => {
      const response = await authenticatedFetch<DefinitionsApiResponse>(`${CACHE_ENDPOINT}/v1/triggers`, {
        skipAuth: true,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch triggers: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  return {
    triggers: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Hook for fetching actions
export function useActions(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: ["actions"],
    queryFn: async () => {
      const response = await authenticatedFetch<DefinitionsApiResponse>(`${CACHE_ENDPOINT}/v1/actions`, {
        skipAuth: true,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch actions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  return {
    actions: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Hook for fetching logic actions
export function useLogicActions(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: ["logic-actions"],
    queryFn: async () => {
      const response = await authenticatedFetch<DefinitionsApiResponse>(`${V2_ENDPOINT}/v1/logic-actions`, {
        optionalAuth: true,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch logic actions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  return {
    logicActions: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Helper hook to get payload schema by type (for editing node configuration)
export function useSchemaByType(type: string) {
  const { triggers } = useTriggers();
  const { actions } = useActions();
  const { logicActions } = useLogicActions();

  const allDefinitions = [...triggers, ...actions, ...logicActions];
  const definition = allDefinitions.find(def => def.type === type);

  return definition?.payloadSchema;
}

// Helper hook to get full definition by type (includes connector info)
export function useDefinitionByType(type: string) {
  const { triggers } = useTriggers();
  const { actions } = useActions();
  const { logicActions } = useLogicActions();

  const allDefinitions = [...triggers, ...actions, ...logicActions];
  return allDefinitions.find(def => def.type === type);
}

// ============================================================================
// Tag-filtered hooks for integrations page
// ============================================================================

export interface UseActionsOptions {
  tags?: string[]; // Filter by tags (AND logic: action must have ALL specified tags)
  enabled?: boolean;
}

export interface UseTriggersOptions {
  tags?: string[]; // Filter by tags (AND logic: trigger must have ALL specified tags)
  enabled?: boolean;
}

/**
 * Hook for fetching actions with optional tag filtering.
 * When tags are provided, uses backend API filtering for efficiency.
 *
 * @param options.tags - Array of tag slugs to filter by (AND logic)
 * @param options.enabled - Whether to enable the query (default: true)
 */
export function useActionsWithTags(options?: UseActionsOptions) {
  const tags = options?.tags || [];
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ["actions", "filtered", tags],
    queryFn: async () => {
      const url = new URL(`${V2_ENDPOINT}/v1/actions`);

      // Add tags as query params (supports comma-separated)
      if (tags.length > 0) {
        url.searchParams.set("tags", tags.join(","));
      }

      const response = await authenticatedFetch<DefinitionsApiResponse>(url.toString(), {
        skipAuth: true,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch actions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  return {
    actions: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching triggers with optional tag filtering.
 * When tags are provided, uses backend API filtering for efficiency.
 *
 * @param options.tags - Array of tag slugs to filter by (AND logic)
 * @param options.enabled - Whether to enable the query (default: true)
 */
export function useTriggersWithTags(options?: UseTriggersOptions) {
  const tags = options?.tags || [];
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ["triggers", "filtered", tags],
    queryFn: async () => {
      const url = new URL(`${V2_ENDPOINT}/v1/triggers`);

      // Add tags as query params (supports comma-separated)
      if (tags.length > 0) {
        url.searchParams.set("tags", tags.join(","));
      }

      const response = await authenticatedFetch<DefinitionsApiResponse>(url.toString(), {
        skipAuth: true,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch triggers: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code !== 0) {
        throw new Error(`API error: ${data.message}`);
      }

      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  return {
    triggers: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Combined hook to fetch both actions and triggers, optionally filtered by tags.
 * Useful for the integrations page where we want to show all integrations.
 *
 * @param options.tags - Array of tag slugs to filter by (AND logic)
 * @param options.type - Filter by type: 'all', 'actions', or 'triggers'
 */
export function useIntegrations(options?: { tags?: string[]; type?: "all" | "actions" | "triggers" }) {
  const tags = options?.tags || [];
  const type = options?.type || "all";

  const { actions, isLoading: actionsLoading } = useActionsWithTags({
    tags,
    enabled: type === "all" || type === "actions",
  });

  const { triggers, isLoading: triggersLoading } = useTriggersWithTags({
    tags,
    enabled: type === "all" || type === "triggers",
  });

  // Combine and add integrationType field
  const integrations = [
    ...(type === "all" || type === "triggers"
      ? triggers.map((t: ActionTriggerDefinition) => ({ ...t, integrationType: "trigger" as const }))
      : []),
    ...(type === "all" || type === "actions"
      ? actions.map((a: ActionTriggerDefinition) => ({ ...a, integrationType: "action" as const }))
      : []),
  ];

  return {
    integrations,
    actions,
    triggers,
    isLoading: actionsLoading || triggersLoading,
  };
}
